import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../utils/api";
import { HiOutlineEye } from "react-icons/hi";
import { FaFileInvoiceDollar } from "react-icons/fa";
import Modal from "../components/Modal";
import AddContract from "../components/contract/AddContract";
import ViewContract from "../components/contract/ViewContract";
import EditContract from "../components/contract/EditContract";
import AddBtn from "../components/AddBtn";
import EditBtn from "../components/EditBtn";
import DeleteBtn from "../components/DeleteBtn";
import RoleAccess from "../components/RoleAccess";

export default function ContractList() {
  const { contentOwnerId } = useParams();
  const navigate = useNavigate();

  const [contracts, setContracts] = useState([]);
  const [contentOwner, setContentOwner] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState({ text: "", date: "" });
  const [textSearchInput, setTextSearchInput] = useState("");
  const [dateSearchInput, setDateSearchInput] = useState("");

  const [selectedContract, setSelectedContract] = useState(null);
  const [isModalOpenForAdd, setIsModalOpenForAdd] = useState(false);
  const [isModalOpenForView, setIsModalOpenForView] = useState(false);
  const [isModalOpenForEdit, setIsModalOpenForEdit] = useState(false);

  const itemsPerPage = 5;

  const fetchContracts = async (page = 1, text = "", date = "") => {
    setLoading(true);
    try {
      const res = await api.get(`/contracts/content-owner/${contentOwnerId}`, {
        params: { page, limit: itemsPerPage, text, date },
        withCredentials: true,
      });

      setContracts(res.data.contracts || []);
      setContentOwner(res.data.contentOwner || null);
      setProduct(res.data.contentOwner?.productId || null);

      if (res.data.pagination) {
        setTotalPages(res.data.pagination.totalPages || 1);
      }
    } catch (err) {
      console.error("Error fetching contracts:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchContentOwner = async () => {
    try {
      const res = await api.get(`/content-owner/${contentOwnerId}`, {
        withCredentials: true,
      });
      setContentOwner(res.data);
      if (res.data.productId) {
        const productRes = await api.get(`/products/${res.data.productId}`);
        setProduct(productRes.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    api
      .get("/users/me", { withCredentials: true })
      .then((res) => setUserRole(res.data.role))
      .catch(() => setUserRole(null));
  }, []);

  useEffect(() => {
    if (!contentOwnerId) return;
    fetchContentOwner();
    fetchContracts(currentPage, search.text, search.date);
  }, [contentOwnerId, currentPage, search]);

  const deleteContract = async (id) => {
    if (!window.confirm("Are you sure you want to delete this contract?"))
      return;

    try {
      await api.delete(`/contracts/${id}`, { withCredentials: true });

      if (contracts.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        setContracts((prev) => prev.filter((c) => c._id !== id));
      }
    } catch (err) {
      console.error(err);
      alert("Failed to delete contract!");
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearch({ text: textSearchInput, date: dateSearchInput });
  };

  const handleClear = () => {
    setTextSearchInput("");
    setDateSearchInput("");
    setCurrentPage(1);
    setSearch({ text: "", date: "" });
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <>
      <Modal
        isOpen={isModalOpenForAdd}
        onClose={() => setIsModalOpenForAdd(false)}
      >
        <AddContract
          contentOwnerId={contentOwnerId}
          productId={product?._id}
          onSuccess={() => {
            setIsModalOpenForAdd(false);
            setCurrentPage(1);
            fetchContracts(1, search);
          }}
        />
      </Modal>

      <Modal
        isOpen={isModalOpenForView}
        onClose={() => setIsModalOpenForView(false)}
      >
        <ViewContract contract={selectedContract} />
      </Modal>

      <Modal
        isOpen={isModalOpenForEdit}
        onClose={() => setIsModalOpenForEdit(false)}
      >
        {selectedContract && (
          <EditContract
            contract={selectedContract}
            onSuccess={() => {
              setIsModalOpenForEdit(false);
              fetchContracts(currentPage, search);
            }}
          />
        )}
      </Modal>

      <div className="flex flex-col items-center mt-10 gap-2">
        {product && (
          <>
            <img
              src={`${process.env.REACT_APP_API_URL}/${product.imageUrl}`}
              alt={product.name}
              className="w-14 h-14 object-cover rounded-xl"
            />
            <h1 className="text-2xl font-bold">{product.name}</h1>
            <h2 className="text-xl font-semibold">Contracts</h2>
          </>
        )}
      </div>

      <div className="ml-2 mt-5 bg-white rounded-md shadow-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-2">
            <RoleAccess allow={["Admin", "Manager"]} role={userRole}>
              <AddBtn
                onClick={() => setIsModalOpenForAdd(true)}
                label="Add Contract"
              />
            </RoleAccess>
            <button
              onClick={() =>
                navigate(
                  `/dashboard/content-owner/${contentOwner._id}/earnings`,
                )
              }
              className="px-4 py-2 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition flex items-center gap-2"
            >
              View Earnings
              <FaFileInvoiceDollar className="h-4 w-4 text-white" />
            </button>
          </div>
          {contentOwner && (
            <div className="text-gray-700">
              Content Owner:{" "}
              <span className="font-bold text-blue-600">
                {contentOwner.name}
              </span>
            </div>
          )}
        </div>
        <div className="mb-4 flex justify-end gap-2">
          <input
            type="text"
            placeholder="Search by contract ID, contract name, or artwork..."
            value={textSearchInput}
            onChange={(e) => setTextSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="w-[450px] px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="date"
            value={dateSearchInput}
            onChange={(e) => setDateSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none"
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition"
          >
            Search
          </button>
          <button
            onClick={handleClear}
            className="px-4 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition"
          >
            Clear
          </button>
        </div>

        <table className="w-full text-sm border-separate border-spacing-y-2">
          <thead>
            <tr className="bg-slate-100 text-slate-600">
              <th className="px-4 py-3 text-left rounded-tl-xl rounded-bl-xl">
                Contract ID
              </th>
              <th className="px-4 py-3 text-center">Contract Name</th>
              <th className="px-4 py-3 text-center">Signing Date</th>
              <th className="px-4 py-3 text-center">Expiry Date</th>
              <th className="px-4 py-3 text-center">Artworks</th>
              <th className="px-4 py-3 text-right rounded-tr-xl rounded-br-xl">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-4">
                  Loading contracts...
                </td>
              </tr>
            ) : contracts.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-4">
                  No contracts found.
                </td>
              </tr>
            ) : (
              contracts.map((contract) => (
                <tr
                  key={contract._id}
                  className="border-t transition hover:bg-slate-100"
                >
                  <td className="px-4 py-4 font-medium text-blue-600">
                    {contract.contractId}
                  </td>
                  <td className="px-4 py-4 text-center">
                    {contract.contractName}
                  </td>
                  <td className="px-4 py-4 text-center">
                    {new Date(contract.signingDate).toLocaleDateString("en-GB")}
                  </td>
                  <td className="px-4 py-4 text-center">
                    {new Date(contract.expiryDate).toLocaleDateString("en-GB")}
                  </td>
                  <td className="px-4 py-4 text-center">
                    {contract.artworks.length}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex items-center justify-end gap-4">
                      <button
                        onClick={() => {
                          setSelectedContract(contract);
                          setIsModalOpenForView(true);
                        }}
                      >
                        <HiOutlineEye className="h-5 w-5 text-green-400 hover:text-green-700 transition transform hover:scale-110" />
                      </button>
                      <RoleAccess allow={["Admin", "Manager"]} role={userRole}>
                        <EditBtn
                          onClick={() => {
                            setSelectedContract(contract);
                            setIsModalOpenForEdit(true);
                          }}
                          label="Edit Contract"
                        />
                        <DeleteBtn
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteContract(contract._id);
                          }}
                          label="Delete Contract"
                        />
                      </RoleAccess>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>
            <span className="px-3 py-1 font-medium">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
}
